import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
} from "reactstrap";

import { compose, withProps } from "recompose";
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
  DirectionsRenderer,
} from "react-google-maps";

import EmpresaContext from "../../../contexts/Empresa";
import AuthContext from "../../../contexts/Auth";
import api from "../../../services/api";

// Componente do mapa
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const MapComponent = compose(
  withProps({
    googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=3.exp&libraries=geometry,drawing,places`,
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `400px` }} />,
    mapElement: <div style={{ height: `100%` }} />,
  }),
  withScriptjs,
  withGoogleMap
)((props) => (
  <GoogleMap
    defaultZoom={12}
    center={props.center}
    zoom={props.zoom}
  >
    {props.markers.map((marker, index) => (
      <Marker
        key={index}
        position={{ lat: marker.lat, lng: marker.lng }}
        title={marker.title}
        label={marker.label}
      />
    ))}
    {props.directions && <DirectionsRenderer directions={props.directions} />}
  </GoogleMap>
));

const DetalhesRota = ({ show, onClose, rotaId, notify, onVoltar }) => {
  const { empresaSelecionada } = useContext(EmpresaContext);
  const { auth } = useContext(AuthContext);

  const [rota, setRota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [directions, setDirections] = useState(null);
  const [center, setCenter] = useState({ lat: -23.5505, lng: -46.6333 });

  useEffect(() => {
    if (show && rotaId && empresaSelecionada?.id) {
      buscarDetalhesRota();
    }
  }, [show, rotaId, empresaSelecionada]);

  const buscarDetalhesRota = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/common/empresas/${empresaSelecionada.id}/rotas-visitas/${rotaId}/detalhes`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      const rotaData = response.data;
      setRota(rotaData);

      // Configurar mapa se houver dados de localização
      if (rotaData.rotaOportunidades && rotaData.rotaOportunidades.length > 0) {
        configurarMapa(rotaData);
      }

    } catch (error) {
      console.error("Erro ao buscar detalhes da rota:", error);
      notify("danger", "Erro ao carregar detalhes da rota");
    } finally {
      setLoading(false);
    }
  };

  const configurarMapa = (rotaData) => {
    try {
      const novosMarkers = [];

      // Ponto de partida
      if (rotaData.latitude_partida && rotaData.longitude_partida) {
        novosMarkers.push({
          lat: parseFloat(rotaData.latitude_partida),
          lng: parseFloat(rotaData.longitude_partida),
          title: "Ponto de Partida",
          label: "P"
        });
      }

      // Pontos das visitas
      let markerIndex = 1;
      rotaData.rotaOportunidades.forEach((item) => {
        const imoveis = item.oportunidade?.imoveis || [];

        imoveis.forEach(imovel => {
          if (imovel.latitude && imovel.longitude) {
            novosMarkers.push({
              lat: parseFloat(imovel.latitude),
              lng: parseFloat(imovel.longitude),
              title: `${markerIndex}. ${imovel.nome || 'Imóvel'} - ${item.oportunidade.cliente.nome}`,
              label: markerIndex.toString()
            });
            markerIndex++;
          }
        });
      });

      // Ponto de destino (se diferente da partida)
      if (rotaData.latitude_destino && rotaData.longitude_destino &&
        (rotaData.latitude_destino !== rotaData.latitude_partida ||
          rotaData.longitude_destino !== rotaData.longitude_partida)) {
        novosMarkers.push({
          lat: parseFloat(rotaData.latitude_destino),
          lng: parseFloat(rotaData.longitude_destino),
          title: "Destino Final",
          label: "D"
        });
      }

      setMarkers(novosMarkers);

      // Ajustar centro do mapa
      if (novosMarkers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        novosMarkers.forEach(marker => {
          bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
        });

        const centerPoint = bounds.getCenter();
        setCenter({
          lat: centerPoint.lat(),
          lng: centerPoint.lng()
        });
      }

    } catch (error) {
      console.error("Erro ao configurar mapa:", error);
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString("pt-BR");
  };

  const formatarDataHora = (dataHora) => {
    if (!dataHora) return "N/A";
    return new Date(dataHora).toLocaleString("pt-BR");
  };

  const formatarTempo = (minutos) => {
    if (!minutos) return "N/A";
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return horas > 0 ? `${horas}h ${mins}min` : `${mins}min`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendente: { color: "secondary", text: "Pendente" },
      realizada: { color: "success", text: "Realizada" },
      cancelada: { color: "danger", text: "Cancelada" },
      em_andamento: { color: "warning", text: "Em Andamento" },
      concluida: { color: "success", text: "Concluída" },
    };

    const config = statusConfig[status] || { color: "secondary", text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const abrirGoogleMaps = () => {
    if (rota?.google_maps_url) {
      window.open(rota.google_maps_url, '_blank');
    } else {
      notify("warning", "URL do Google Maps não disponível para esta rota");
    }
  };

  const formatarEndereco = (cliente) => {
    if (!cliente) return "N/A";

    if (cliente.endereco?.endereco_formatado) {
      return cliente.endereco.endereco_formatado;
    }

    const partes = [];
    if (cliente.endereco?.logradouro) partes.push(cliente.endereco.logradouro);
    if (cliente.endereco?.numero) partes.push(cliente.endereco.numero);
    if (cliente.endereco?.bairro) partes.push(cliente.endereco.bairro);
    if (cliente.endereco?.cidade) partes.push(cliente.endereco.cidade);
    if (cliente.endereco?.estado) partes.push(cliente.endereco.estado);

    return partes.length > 0 ? partes.join(", ") : "Endereço não informado";
  };

  if (!show) return null;

  return (
    <Modal isOpen={show} toggle={onClose} size="xl" style={{ maxWidth: "95%" }}>
      <ModalHeader toggle={onClose}>
        <div className="d-flex align-items-center">
          <Button
            color="link"
            size="sm"
            onClick={onVoltar}
            className="p-0 mr-2"
          >
            ← Voltar
          </Button>
          Detalhes da Rota {rota ? `#${rota.id}` : ""}
        </div>
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Carregando detalhes...</p>
          </div>
        ) : !rota ? (
          <div className="text-center py-4">
            <p>Rota não encontrada.</p>
          </div>
        ) : (
          <Row>
            {/* Seção 1 - Informações Gerais */}
            <Col md="4">
              <Card className="mb-4">
                <CardHeader>
                  <h6 className="mb-0">Informações Gerais</h6>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <strong>ID da Rota:</strong> #{String(rota.id).padStart(4, '0')}
                  </div>
                  <div className="mb-3">
                    <strong>Colaborador:</strong> {rota.colaborador?.name || "N/A"}
                  </div>
                  <div className="mb-3">
                    <strong>Período:</strong><br />
                    {formatarData(rota.data_inicio)}
                    {rota.data_fim !== rota.data_inicio && ` até ${formatarData(rota.data_fim)}`}
                  </div>
                  <div className="mb-3">
                    <strong>Tempo Estimado:</strong> {formatarTempo(rota.tempo_estimado_minutos)}
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong> {getStatusBadge(rota.estatisticas?.status_geral)}
                  </div>

                  {/* Estatísticas */}
                  <div className="mb-3">
                    <strong>Progresso das Visitas:</strong>
                    <div className="small text-muted">
                      <div>Total: {rota.estatisticas?.total_visitas || 0}</div>
                      <div>Realizadas: {rota.estatisticas?.visitas_realizadas || 0}</div>
                      <div>Pendentes: {rota.estatisticas?.visitas_pendentes || 0}</div>
                      {rota.estatisticas?.visitas_canceladas > 0 && (
                        <div>Canceladas: {rota.estatisticas.visitas_canceladas}</div>
                      )}
                    </div>
                  </div>

                  {rota.observacoes && (
                    <div className="mb-3">
                      <strong>Observações:</strong>
                      <p className="text-muted small mt-1">{rota.observacoes}</p>
                    </div>
                  )}

                  {rota.google_maps_url && (
                    <Button
                      color="success"
                      size="sm"
                      block
                      onClick={abrirGoogleMaps}
                    >
                      Abrir no Google Maps
                    </Button>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Seção 2 - Mapa */}
            <Col md="8">
              <Card className="mb-4">
                <CardHeader>
                  <h6 className="mb-0">Mapa da Rota</h6>
                </CardHeader>
                <CardBody>
                  <MapComponent
                    markers={markers}
                    directions={directions}
                    center={center}
                    zoom={12}
                  />
                </CardBody>
              </Card>
            </Col>

            {/* Seção 3 - Lista de Visitas */}
            <Col md="12">
              <Card>
                <CardHeader>
                  <h6 className="mb-0">Lista de Visitas</h6>
                </CardHeader>
                <CardBody>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Ordem</th>
                        <th>Cliente/Oportunidade</th>
                        <th>Imóveis</th>
                        <th>Endereço</th>
                        <th>Status</th>
                        <th>Data/Hora Realização</th>
                        <th>Observações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rota.rotaOportunidades?.map((item) => (
                        <tr key={item.id}>
                          <td>#{item.ordem}</td>
                          <td>
                            <div>
                              <strong>{item.oportunidade?.cliente?.nome || "Cliente"}</strong>
                            </div>
                            <small className="text-muted">
                              {item.oportunidade?.nome || "Oportunidade"}
                            </small>
                          </td>
                          <td>
                            {item.oportunidade?.imoveis && item.oportunidade.imoveis.length > 0 ? (
                              <div>
                                {item.oportunidade.imoveis.map((imovel, idx) => (
                                  <div key={imovel.id} className="small">
                                    <i className="fas fa-map-marker-alt text-primary mr-1"></i>
                                    {imovel.nome || `Imóvel #${imovel.id}`}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <small className="text-muted">Sem imóveis cadastrados</small>
                            )}
                          </td>
                          <td>
                            <small>{formatarEndereco(item.oportunidade?.cliente)}</small>
                          </td>
                          <td>{getStatusBadge(item.status_visita)}</td>
                          <td>{formatarDataHora(item.data_hora_visita)}</td>
                          <td>
                            <small>{item.observacoes_visita || "-"}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </ModalBody>
    </Modal>
  );
};

export default DetalhesRota;
